/* One table of the agent open in its Spaces tab: the toolbar over the Data Table, a page of
   rows from the server (search, filters and sorting are the server's), every edit saved as
   it is made and what a rule refused said in the footer. People is read-only and pages on
   who came last. */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { useSpaceRows, useUpdateRows, useDeleteRows } from "@/shared/api/spaces";
import { IconButton } from "@/shared/components/ui-kit";

import ColumnEditor, { type ColumnTarget } from "./ColumnEditor";
import DataTable, { type CellWrite } from "./table/DataTable";
import TableTile from "./TableTile";
import TableToolbar, { type SpaceView } from "./TableToolbar";
import { keptOnRow, repeatedNames } from "./table/columns";

import type { Agent, SpaceColumn, SpaceCondition, SpaceRowResult, SpaceTable } from "@/shared/types";

const PAGE = 50;
const SEARCH_WAIT_MS = 250;

/** Hidden columns, per table, in this browser: a convenience, so a failed read is no loss. */
function readHidden(tableId: string): string[] {
  try {
    return JSON.parse(window.localStorage.getItem(`spaces:hidden:${tableId}`) ?? "[]");
  } catch {
    return []; // private window or blocked storage: every column shows
  }
}
function saveHidden(tableId: string, hidden: string[]) {
  try {
    window.localStorage.setItem(`spaces:hidden:${tableId}`, JSON.stringify(hidden));
  } catch {
    // storage refused: the choice holds until the page reloads
  }
}

export default function TableView({ agent, table, tables, rowOpen, onOpenRow, onNewRow, onView }: {
  agent: Agent;
  table: SpaceTable;
  /** A row is open beside the table: the toolbar narrows. */
  rowOpen: boolean;
  /** Every table of the space: a link column's editor picks one. */
  tables: SpaceTable[];
  onOpenRow: (rowId: string) => void;
  onNewRow: () => void;
  onView: (view: SpaceView) => void;
}) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [conditions, setConditions] = useState<SpaceCondition[]>([]);
  const [sort, setSort] = useState<string | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [before, setBefore] = useState<string[]>([]); // People: the cursors of the pages passed
  const [hidden, setHiddenState] = useState<string[]>(() => readHidden(table.id));
  const [editor, setEditor] = useState<ColumnTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const wait = window.setTimeout(() => {
      setQuery(search.trim());
      setOffset(0);
      setBefore([]);
    }, SEARCH_WAIT_MS);
    return () => window.clearTimeout(wait);
  }, [search]);

  const complete = conditions.filter(
    (condition) => condition.column && (["empty", "not_empty"].includes(condition.op) || String(condition.value ?? "") !== ""),
  );
  const { data: page, isFetching } = useSpaceRows(agent.id, table.id, {
    search: query || undefined,
    where: complete,
    sort,
    offset,
    limit: PAGE,
    before: before[before.length - 1] ?? null,
  });
  const update = useUpdateRows(agent.id, table.id);
  const remove = useDeleteRows(agent.id, table.id);

  const columns = useMemo(
    () => table.columns.filter((column, index) => index === 0 || !hidden.includes(column.key)),
    [table.columns, hidden],
  );
  const setHidden = (next: string[]) => {
    setHiddenState(next);
    saveHidden(table.id, next);
  };

  const write = async (writes: CellWrite[]) => {
    const byRow = new Map<string, Record<string, unknown>>();
    for (const { rowId, key, value } of writes) byRow.set(rowId, { ...byRow.get(rowId), [key]: value });
    try {
      const results = await update.mutateAsync([...byRow].map(([id, values]) => ({ id, values })));
      setNotice(refusal(results, table.columns));
    } catch {
      // the query cache has shown the failure; the cells refetch as the server holds them
    }
  };

  const pager = table.system ? (
    <PagerButtons
      label={before.length ? "Older people" : "Newest people"}
      back={before.length > 0 ? () => setBefore(before.slice(0, -1)) : undefined}
      next={page?.next_before && (page.rows.length ?? 0) >= PAGE ? () => setBefore([...before, page.next_before as string]) : undefined}
    />
  ) : (
    <PagerButtons
      label={page?.total ? `${offset + 1}–${Math.min(offset + PAGE, page.total)} of ${page.total.toLocaleString("en-US")}` : "0 rows"}
      back={offset > 0 ? () => setOffset(Math.max(0, offset - PAGE)) : undefined}
      next={page?.total && offset + PAGE < page.total ? () => setOffset(offset + PAGE) : undefined}
    />
  );

  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
      <TableToolbar
        columns={table.columns}
        search={search}
        onSearch={setSearch}
        conditions={conditions}
        onConditions={(next) => {
          setConditions(next.filter((condition) => !condition.column || keptOnRow(table.columns.find((c) => c.key === condition.column) as SpaceColumn)));
          setOffset(0);
          setBefore([]);
        }}
        hidden={hidden}
        onHidden={setHidden}
        onAddColumn={table.system ? undefined : () => setEditor({ column: null, anchor: null })}
        onView={onView}
        narrow={rowOpen}
      />
      <div className="flex-1 min-h-0 flex px-5 pt-3.5 pb-5">
        <DataTable
          title={table.name}
          icon={<TableTile system={table.system} />}
          sub={
            <span className="text-[13px] text-[var(--dt-muted)] whitespace-nowrap truncate">
              {table.system ? "Kept by Definable" : `${(page?.total ?? table.row_count ?? 0).toLocaleString("en-US")} rows`}
              {isFetching && <span className="sr-only"> · loading</span>}
            </span>
          }
          columns={columns}
          rows={page?.rows ?? []}
          people={page?.people ?? {}}
          editable={!table.system}
          sort={sort}
          onSort={(key) => {
            setSort(sort === key ? `-${key}` : sort === `-${key}` ? undefined : key);
            setOffset(0);
            setBefore([]);
          }}
          onWrite={write}
          onAddRow={onNewRow}
          onDeleteRows={(ids) => remove.mutate(ids)}
          onOpenRow={(rowId) => !table.system && onOpenRow(rowId)}
          onColumnMenu={table.system ? undefined : (column, anchor) => setEditor({ column, anchor })}
          openColumn={editor?.column?.key ?? null}
          pager={pager}
          notice={notice && <span role="alert" className="truncate text-[var(--danger)]">{notice}</span>}
        />
      </div>
      {editor && <ColumnEditor agentId={agent.id} table={table} tables={tables} target={editor} onClose={() => setEditor(null)} />}
    </div>
  );
}

/** What the rules refused of a write, in a line; null when everything was saved. */
function refusal(results: SpaceRowResult[], columns: SpaceColumn[]): string | null {
  const failed = results.find((result) => result.status === "refused" || result.status === "repeat");
  if (!failed) return null;
  if (failed.status === "refused") return failed.problem;
  return `Another row already has this ${repeatedNames(failed.repeated, columns)}`;
}

function PagerButtons({ label, back, next }: { label: string; back?: () => void; next?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono">{label}</span>
      <IconButton aria-label="Previous page" disabled={!back} onClick={back} className="disabled:opacity-35 disabled:pointer-events-none">
        <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
      </IconButton>
      <IconButton aria-label="Next page" disabled={!next} onClick={next} className="disabled:opacity-35 disabled:pointer-events-none">
        <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
      </IconButton>
    </span>
  );
}
